"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  DilationPanel,
  SpeedPanel,
  TripPanel,
} from "@/components/tools/physics-fields/relativity-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const TIME_DILATION_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeDilation: "Lorentz factor, time dilation and length contraction",
  modeTrip: "Round trip (twin paradox)",
  modeSpeed: "Find the speed from γ or a ratio",
  exampleLabel: "Start from an example",
  custom: "Custom values",
  dilationExamples: {
    fast: "A traveller at 0.8c: one year, and a 4 light-year length",
    muon: "A muon at 98% of c: its 2.197 µs lifetime",
    gps: "A GPS satellite at 3.874 km/s: one day",
    jet: "A jet at 900 km/h: ten hours",
  },
  tripExamples: {
    four: "A star 4 light-years away at 0.8c",
    proxima: "Proxima Centauri (4.25 ly) at 0.9c",
    sirius: "Sirius (8.6 ly) at 0.99c",
    vega: "Vega (25 ly) at 0.999c",
  },
  unitFor: "Unit for {name}",
  speed: "Speed v",
  speedHint: "Below the speed of light. In units of c, 0.8 means 80% of c.",
  properTime: "Proper time Δt₀ (optional)",
  properTimeHint: "Time measured by the clock that moves with the object.",
  properLength: "Proper length L₀ (optional)",
  properLengthHint: "Length measured at rest, along the direction of motion.",
  knownLabel: "What do you know?",
  knownTime: "A dilated and a proper time",
  knownLength: "A proper and a contracted length",
  knownGamma: "The Lorentz factor γ",
  dilatedTimeInput: "Dilated time Δt (the longer one)",
  properTimeInput: "Proper time Δt₀ (the shorter one)",
  properLengthInput: "Proper length L₀ (the longer one)",
  contractedLengthInput: "Contracted length L (the shorter one)",
  gammaInput: "Lorentz factor γ",
  gammaHint: "At least 1.",
  showSpeedIn: "Show the speed in",
  tripDistance: "One-way distance d, in the Earth frame",
  tripDistanceHint: "To the turning point; the trip is out and back.",
  showTimesIn: "Show times in",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  gamma: "Lorentz factor γ",
  beta: "Speed as a fraction of c, β",
  gammaMinusOne: "γ − 1",
  dilatedTime: "Dilated time Δt = γΔt₀",
  drift: "Clocks drift apart by Δt − Δt₀",
  contractedLength: "Contracted length L = L₀/γ",
  speedResult: "Speed v",
  ageDifference: "The traveller is younger by",
  earthTime: "Round trip by Earth's clocks",
  travellerTime: "Round trip by the traveller's clock",
  contractedDistance: "One-way distance the traveller sees, d/γ",
  speedKmS: "Speed",
  earthClock: "Earth clocks",
  travellerClock: "Traveller's clock",
  working: "The working",
  curveLabel: "The Lorentz factor against speed",
  curveCaption:
    "γ stays close to 1 until v is a large fraction of c, then climbs steeply (the vertical axis is logarithmic).",
  curveDescription:
    "A curve of the Lorentz factor gamma, on a logarithmic vertical axis from 1 to 50, against speed as a fraction of the speed of light from 0 to 1, rising slowly at first and then sharply near 1, with the chosen speed marked by a dot.",
  tripLabel: "Elapsed time for the Earth and the traveller",
  tripCaption:
    "The bars show the same round trip on the two clocks. Acceleration and turnaround are idealised as instantaneous.",
  tripDescription:
    "Two horizontal bars: a long one for the time elapsed on Earth and a shorter one for the time elapsed on the travelling twin's clock.",
  errorMissing:
    "Enter the speed. Proper time and proper length are optional. Scientific notation such as 3e5 works.",
  errorPositive:
    "Times, lengths, the Lorentz factor and the distance must be positive.",
  errorNegativeSpeed:
    "Speed cannot be negative. Enter its size; the direction does not matter here.",
  errorLightSpeed:
    "Nothing with mass can reach or pass the speed of light. Enter a speed below c (for example 0.999 c).",
  errorGamma:
    "The Lorentz factor cannot be below 1 (that would mean time running faster). Check which value is the dilated time and which is the proper time.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Special relativity only: constant velocity in a flat spacetime. Gravitational time dilation (for GPS, about +45 µs per day for the satellites) is a separate effect from general relativity, and turnaround acceleration in the twin trip is idealised as instantaneous. The speed of light is exactly 299 792 458 m/s.",
} as const;

type Mode = "dilation" | "trip" | "speed";

export default function TimeDilationCalculatorWidget() {
  const strings = useToolUi(TIME_DILATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("dilation");
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="td-mode"
        onChange={setMode}
        options={[
          { value: "dilation", label: strings.modeDilation },
          { value: "trip", label: strings.modeTrip },
          { value: "speed", label: strings.modeSpeed },
        ]}
        value={mode}
      />
      {mode === "dilation" ? (
        <DilationPanel sig={sig} strings={strings} />
      ) : null}
      {mode === "trip" ? <TripPanel sig={sig} strings={strings} /> : null}
      {mode === "speed" ? <SpeedPanel sig={sig} strings={strings} /> : null}
      <SigFigsField
        format={fmt}
        id="td-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
